import ks from "@shared/kalmstromShared4";
import { IListDefinition } from "./DBInterfaces";

export default class DBStringUtils {

    public static EscapeHtml = (unsafe: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (unsafe === null || unsafe === undefined || unsafe === "") { return ""; }
            return String(unsafe)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#039;");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBStringUtils.EscapeHtml");
            return "";
        }
    };

    public static EscapeXml = (str: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (str === null || str === undefined || str === "") { return ""; }
            return String(str)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&apos;");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBStringUtils.EscapeXml");
            return "";
        }
    };

    public static EscapePowerShellString = (str: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (str === null || str === undefined || str === "") { return ""; }
            return String(str)
                .replace(/`/g, "``")
                .replace(/"/g, '`"')
                .replace(/\$/g, "`$$")
                .replace(/\n/g, "`n")
                .replace(/\r/g, "`r")
                .replace(/\t/g, "`t");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBStringUtils.EscapePowerShellString");
            return "";
        }
    };

    public static ToPascalCase = (str: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (!str) { return ""; }
            const UpperCaseWord = (word: string): string => {
                return word.toUpperCase();
            };
            return str
                .replace(/(?:^\w|[A-Z]|\b\w)/g, UpperCaseWord)
                .replace(/\s+/g, "")
                .replace(/[^a-zA-Z0-9]/g, "");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBStringUtils.ToPascalCase");
            return "";
        }
    };

    public static Singularize = (word: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (!word) { return ""; }
            const w: string = word.trim();
            if (w.length <= 2) { return w; }
            if (w.endsWith("ies")) { return w.slice(0, -3) + "y"; }
            if (w.endsWith("ves")) { return w.slice(0, -3) + "f"; }
            const endsWithEs: boolean = w.endsWith("ches") || w.endsWith("shes") || w.endsWith("ses") || w.endsWith("xes") || w.endsWith("zes");
            if (endsWithEs) { return w.slice(0, -2); }
            const endsWithS: boolean = w.endsWith("s") && !w.endsWith("ss") && !w.endsWith("us");
            if (endsWithS) { return w.slice(0, -1); }
            return w;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBStringUtils.Singularize");
            return word;
        }
    };

    public static GetContentTypeName = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (list.ContentTypeName) { return list.ContentTypeName; }
            return "CT" + DBStringUtils.Singularize(list.Title).replace(/\s+/g, "");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBStringUtils.GetContentTypeName");
            return "";
        }
    };

    public static GenerateDeterministicGuid = (seed: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let hash: number = 0;
            for (let i: number = 0; i < seed.length; i++) {
                const charCode: number = seed.charCodeAt(i);
                hash = ((hash << 5) - hash) + charCode;
                hash = hash & hash;
            }
            const h: number = Math.abs(hash);
            const hex: string = h.toString(16).padStart(8, "0");
            const s1: string = hex.substring(0, 8);

            let h2: number = 0;
            for (let i: number = 0; i < seed.length; i++) {
                h2 = ((h2 << 7) - h2) + seed.charCodeAt(seed.length - 1 - i);
                h2 = h2 & h2;
            }
            const hex2: string = Math.abs(h2).toString(16).padStart(8, "0");
            const s2: string = hex2.substring(0, 4);
            const s3: string = "4" + hex2.substring(4, 7);

            let h3: number = 0;
            for (let i: number = 0; i < seed.length; i++) {
                h3 = ((h3 << 3) + h3) + seed.charCodeAt(i) * (i + 1);
                h3 = h3 & h3;
            }
            const hex3: string = Math.abs(h3).toString(16).padStart(16, "0");
            const s4Num: number = parseInt(hex3.substring(0, 4), 16) & 0x3fff | 0x8000;
            const s4: string = s4Num.toString(16);
            const s5: string = hex3.substring(4, 16);

            return `{${s1}-${s2}-${s3}-${s4}-${s5}}`;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBStringUtils.GenerateDeterministicGuid");
            return "{00000000-0000-4000-8000-000000000000}";
        }
    };

    public static GenerateContentTypeId = (appCode: string, listTitle: string, ctName: string, parentId: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const guid: string = DBStringUtils.GenerateDeterministicGuid(appCode + "_CT_" + listTitle + "_" + ctName);
            const hexGuid: string = guid.replace(/[{}\-]/g, "");
            return parentId + "00" + hexGuid;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBStringUtils.GenerateContentTypeId");
            return "";
        }
    };

    public static UpdateInternalName = (displayName: string, appCode: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const pascalName: string = DBStringUtils.ToPascalCase(displayName);
            return appCode + pascalName;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBStringUtils.UpdateInternalName");
            return "";
        }
    };
}
